import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { ExchangeReasonCode, SalesDocumentSummary } from '@aeris/contracts'
import { formatMoney, fromString, MONEY_SCALE } from '@aeris/money'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  RadioGroup,
  Spinner,
  Textarea,
} from '@aeris/ui'

import { createExchangeRequest } from '../lib/api/exchanges.ts'
import { ApiError, fetchSalesDocument } from '../lib/api.ts'
import { documentRef } from '../lib/sales-labels.ts'

import {
  EXCHANGE_KIND_OPTIONS,
  KIND_CARDS_CLASS,
} from './exchange-kind-options.ts'
import { ExchangeReasonPicker } from './exchange-reason-picker.tsx'

/**
 * O caixa (ou o vendedor) pede uma troca ou um vale ao trocador (F126, fatia 2d).
 *
 * Só o que se sabe no balcão: qual venda, se o cliente quer vale ou outra peça,
 * e por quê. As peças, se voltam ao estoque e quanto vale são decisão do
 * trocador — é a separação do balcão (ADR-0027) aplicada à devolução: quem
 * atende o cliente não é quem mexe em estoque e saldo.
 *
 * F136: a venda aparece com os itens — o caixa confere que é a compra certa
 * antes de mandar —, os motivos de todo dia são um toque, e depois de enviar a
 * tela diz para onde o pedido foi, em vez de só fechar.
 */

const shortDate = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' })

const quantityText = (wire: string): string =>
  wire.replace(/\.?0+$/, '').replace('.', ',')

export type ExchangeRequestDialogProps = {
  sale: {
    id: string
    /** Venda ou pedido (F160): o pedido entregue também pode pedir troca. */
    kind: SalesDocumentSummary['kind']
    number: number | null
    customerName: string
  }
  onDone: () => void
  onClose: () => void
}

export const ExchangeRequestDialog = ({
  sale,
  onDone,
  onClose,
}: ExchangeRequestDialogProps): ReactNode => {
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<'voucher' | 'exchange'>('voucher')
  /*
   * O código é o que se CONTA; o texto é o que se LÊ (F158).
   *
   * Antes eram a mesma coisa: tocar num motivo comum apenas PREENCHIA a caixa
   * de texto, que a pessoa seguinte podia reescrever. O relatório que o dono
   * pediu não conseguia agrupar nada — 66 motivos em 718 trocas, e os 66 em
   * grafias diferentes.
   */
  const [reasonCode, setReasonCode] = useState<ExchangeReasonCode | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const detail = useQuery({
    queryKey: ['sales-document', sale.id],
    queryFn: async ({ signal }) => await fetchSalesDocument(sale.id, signal),
  })
  const document = detail.data?.document

  const send = useMutation({
    mutationFn: async () =>
      await createExchangeRequest({
        originalSaleId: sale.id,
        kind,
        // Não-nulo: o botão de enviar fica desabilitado sem ele.
        reasonCode: reasonCode ?? 'outro',
        ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
      }),
    onSuccess: async () => {
      setSent(true)
      await queryClient.invalidateQueries({ queryKey: ['exchanges'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível enviar o pedido.'
      )
    },
  })

  return (
    <Dialog open onOpenChange={sent ? onDone : onClose}>
      <DialogContent
        title="Pedir troca ou vale"
        description="O trocador confere as peças e conclui."
        className="max-w-xl"
      >
        {sent ? (
          <div className="flex flex-col gap-4">
            <InlineAlert tone="success" title="Pedido enviado ao trocador">
              {`Aparece em Trocas e vales, com o motivo. ${documentRef(sale.kind, sale.number)} · ${sale.customerName}.`}
            </InlineAlert>
            <DialogFooter>
              <Button variant="primary" onClick={onDone}>
                Pronto
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              if (reasonCode === null) return
              setError(null)
              send.mutate()
            }}
          >
            <section
              aria-label="A venda"
              className="flex flex-col gap-1 rounded-(--radius-md) bg-(--surface-sunken) px-3 py-2 text-(length:--text-sm)"
            >
              <span className="font-(--weight-medium)">
                {`${documentRef(sale.kind, sale.number)} · ${sale.customerName}`}
              </span>
              {detail.isPending ? (
                <span className="flex items-center gap-2 text-(--text-muted)">
                  <Spinner /> Carregando os itens…
                </span>
              ) : document === undefined ? null : (
                <>
                  <span className="text-(length:--text-xs) text-(--text-muted)">
                    {`${shortDate.format(new Date(document.issuedAt))} · ${formatMoney(fromString(document.totalAmount, MONEY_SCALE))}`}
                  </span>
                  <ul className="flex flex-col text-(--text-secondary)">
                    {document.lines.slice(0, 4).map((line) => (
                      <li key={line.id} className="truncate">
                        {`${quantityText(line.quantity)} × ${line.description}`}
                      </li>
                    ))}
                    {document.lines.length > 4 ? (
                      <li className="text-(--text-muted)">
                        {`e mais ${String(document.lines.length - 4)}`}
                      </li>
                    ) : null}
                  </ul>
                </>
              )}
            </section>

            <Field label="O cliente quer">
              {(field) => (
                <RadioGroup
                  id={field.id}
                  name="request-kind"
                  orientation="horizontal"
                  value={kind}
                  onValueChange={setKind}
                  options={EXCHANGE_KIND_OPTIONS}
                  className={KIND_CARDS_CLASS}
                />
              )}
            </Field>

            <Field label="Motivo" error={error ?? undefined}>
              {(field) => (
                <div className="flex flex-col gap-2">
                  <ExchangeReasonPicker
                    value={reasonCode}
                    onValueChange={setReasonCode}
                  />
                  {/*
                   * O detalhe agora é OPCIONAL, e o rótulo diz isso.
                   *
                   * Exigir a frase junto do código transformaria um toque em
                   * digitação, e digitação com pressa vira "troca" escrito
                   * cinco vezes. Quem tem o que contar continua tendo onde.
                   */}
                  <Textarea
                    {...field}
                    value={reason}
                    rows={3}
                    maxLength={500}
                    placeholder="Detalhe, se houver: o que aconteceu com a peça"
                    onChange={(event) => {
                      setReason(event.target.value)
                    }}
                  />
                </div>
              )}
            </Field>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={reasonCode === null}
                loading={send.isPending}
                leadingIcon={<Send aria-hidden="true" className="h-4 w-4" />}
              >
                Enviar ao trocador
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
